import React, { useEffect, useMemo, useState } from 'react';
import {
    DrawerLink,
    DrawerTitle,
    Sidebar,
    SidebarBottomSection,
    SidebarDivider,
    SidebarDrawer,
    SidebarIcon,
    SidebarLogo,
    SidebarLogout,
    SidebarTopSection,
    SidebarUserPreferences,
} from './Sidebar';
import Cog from '../../../Assets/Icons/settings.svg?react';
import AdUnitsOutlinedIcon from '@mui/icons-material/AdUnitsOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import UserIcon from '../../../Assets/Icons/user.svg?react';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useHasFullAccess, useHasViewAccess, useUserState } from '../../../Contexts/User';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppRoutes, PathName, Support_ReportRoutes, UserRoutes } from '../../../CommonUtils/Routes';
import { UserPermissions } from '../../../API/Enums';
import { useLocalization } from '../../../Contexts/Localization';
import PieChart from '../../../Assets/Icons/pie-chart.svg?react';
import css from './Sidebar.module.scss';
import { JSXCase, JSXSwitch } from '../../../CommonControls/JSXSwitch/JSXSwitch';

enum SidebarDrawerType {
    None = 0,
    Settings = 1,
    Reports = 2
}

export const SupportSidebar: React.FC = () => {
    const Localization = useLocalization();
    const hasViewAccess = useHasViewAccess();
    const hasFullAccess = useHasFullAccess();
    const location = useLocation();
    const navigate = useNavigate();
    const [openDrawer, setOpenDrawer] = useState<SidebarDrawerType>(SidebarDrawerType.None);
    const { UserData } = useUserState();

    const kioskRouteActive: boolean = location.pathname.includes(AppRoutes.Kiosks);
    const userRouteActive: boolean = location.pathname.includes(AppRoutes.Users);
    const healthCheckRouteActive: boolean = location.pathname.includes(AppRoutes.HealthCheck);
    const responseLogRouteActive: boolean = location.pathname.includes(AppRoutes.ResponseLog);
    const settingsRouteActive: boolean = location.pathname.includes(AppRoutes.KioskBanners) ||
        location.pathname.includes(AppRoutes.DealerGroups) ||
        location.pathname.includes(AppRoutes.FortellisSwapTables) ||
        location.pathname.includes(AppRoutes.DealerSettings) ||
        location.pathname.includes(AppRoutes.SMSWhitelist) ||
        location.pathname.includes(AppRoutes.HunterVehicles);
    const reportsRouteActive: boolean = location.pathname.includes(AppRoutes.Reports);

    useEffect(() => {
        setOpenDrawer(SidebarDrawerType.None);
    }, [location.pathname]);

    const kioskAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.SupportKiosk);
    }, [hasViewAccess]);

    const healthCheckAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.SupportHealthCheck);
    }, [hasViewAccess]);

    const userManagementAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.SupportUserManagement);
    }, [hasViewAccess]);

    const dashboardAccess = useMemo<boolean>(() => {
        return hasFullAccess(UserPermissions.SupportDashboard);
    }, [hasFullAccess]);

    const responseLogAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.SupportResponseLog);
    }, [hasViewAccess]);

    const kioskBannersAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.SupportKioskBanner);
    }, [hasViewAccess]);

    const dealerGroupAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.SupportDealerGroup);
    }, [hasViewAccess]);

    const dealerSettingsAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.SupportDealerSettings);
    }, [hasViewAccess]);

    const reportsAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.SupportReports);
    }, [hasViewAccess]);

    const smsWhitelistAccess: boolean = true;

    const fortellisSwapTablesAccess = useMemo<boolean>(() => {
        return hasViewAccess(UserPermissions.SupportFortellisSwapTables);
    }, [hasViewAccess]);

    const hunterVehiclesAccess: boolean = true;

    const settingsAccess: boolean = kioskBannersAccess || dealerGroupAccess || dealerSettingsAccess;
    const dealerManagementAccess: boolean = dealerGroupAccess || dealerSettingsAccess;
    const kioskUIAccess: boolean = kioskBannersAccess;
    const miscAccess: boolean = fortellisSwapTablesAccess || smsWhitelistAccess || hunterVehiclesAccess;

    const onDrawerLinkClick = (route: string) => {
        if (location.pathname !== route) {
            navigate(route);
        }
        else {
            setOpenDrawer(SidebarDrawerType.None);
        }
    };

    return (
        <Sidebar>
            <SidebarDrawer
                open={openDrawer !== SidebarDrawerType.None}
                onClose={() => setOpenDrawer(SidebarDrawerType.None)}
            >
                <JSXSwitch>
                    <JSXCase condition={openDrawer === SidebarDrawerType.Settings}>
                        {dealerManagementAccess ?
                            <>
                                <DrawerTitle
                                    label={Localization.DealerManagement_IGNORE}
                                />
                                {dealerSettingsAccess ?
                                    <DrawerLink
                                        label={Localization.DealerSettings}
                                        onClick={() => onDrawerLinkClick(AppRoutes.DealerSettings)}
                                    /> : null
                                }
                                {/* <DrawerLink
                                    label={Localization.GoalPerformance}
                                    onClick={() => onDrawerLinkClick(AppRoutes.GoalPerformance)}
                                />
                                <DrawerLink
                                    label={Localization.HunterSettings}
                                    onClick={() => onDrawerLinkClick(AppRoutes.HunterSettings)}
                                />*/}
                                {dealerGroupAccess ?
                                    <DrawerLink
                                        label={Localization.DealerGroups_IGNORE}
                                        onClick={() => onDrawerLinkClick(AppRoutes.DealerGroups)}
                                    /> : null
                                }

                            </> : null
                        }

                        {kioskUIAccess ?
                            <>
                                <DrawerTitle
                                    label={Localization.KioskUI_IGNORE}
                                />
                                {/* <DrawerLink
                                    label={Localization.Services}
                                    onClick={() => onDrawerLinkClick(AppRoutes.KioskServices)}
                                />*/}
                                {kioskBannersAccess ?
                                    <DrawerLink
                                        label={Localization.Banners_IGNORE}
                                        onClick={() => onDrawerLinkClick(AppRoutes.KioskBanners)}
                                    /> : null
                                }
                                {/* <DrawerLink
                                    label={Localization.KioskHours}
                                    onClick={() => onDrawerLinkClick(AppRoutes.KioskHours)}
                                />*/}
                            </> : null
                        }

                        {miscAccess ?
                            <>
                                <DrawerTitle
                                    label={Localization.Miscellaneous}
                                />
                                {fortellisSwapTablesAccess ?
                                    <DrawerLink
                                        label={Localization.FortellisSwapTables}
                                        onClick={() => onDrawerLinkClick(AppRoutes.FortellisSwapTables)}
                                    /> : null
                                }
                                {hunterVehiclesAccess ?
                                    <DrawerLink
                                        label={Localization.HunterVehicles_IGNORE}
                                        onClick={() => onDrawerLinkClick(AppRoutes.HunterVehicles)}
                                    /> : null
                                }
                                <DrawerLink
                                    label={Localization.Services_IGNORE}
                                    onClick={() => onDrawerLinkClick(AppRoutes.Services)}
                                />
                                {smsWhitelistAccess ?
                                    <DrawerLink
                                        label={Localization.SMSWhitelist_IGNORE}
                                        onClick={() => onDrawerLinkClick(AppRoutes.SMSWhitelist)}
                                    /> : null
                                }
                            </> : null
                        }
                    </JSXCase>
                    <JSXCase condition={openDrawer === SidebarDrawerType.Reports}>
                        <DrawerTitle
                            label={Localization.Reports}
                        />
                        <DrawerLink
                            label={Localization.CheckInCompletion_IGNORE}
                            onClick={() => onDrawerLinkClick(PathName(AppRoutes.Reports, Support_ReportRoutes.CheckInCompletion))}
                        />
                        <DrawerLink
                            label={Localization.CheckInSelections_IGNORE}
                            onClick={() => onDrawerLinkClick(PathName(AppRoutes.Reports, Support_ReportRoutes.CheckInSelections))}
                        />
                        <DrawerLink
                            label={Localization.Pickup_IGNORE}
                            onClick={() => onDrawerLinkClick(PathName(AppRoutes.Reports, Support_ReportRoutes.Pickup))}
                        />
                        <DrawerLink
                            label={Localization.NotificationDeliveryStatus_IGNORE}
                            onClick={() => onDrawerLinkClick(PathName(AppRoutes.Reports, Support_ReportRoutes.NotificationDeliveryStatus))}
                        />
                    </JSXCase>
                </JSXSwitch>
            </SidebarDrawer>
            <SidebarUserPreferences />
            <SidebarDivider />
            <SidebarTopSection>
                <SidebarIcon
                    show={kioskAccess}
                    active={kioskRouteActive}
                    tooltip={Localization.KioskConfigurations_IGNORE}
                    onClick={() => navigate(AppRoutes.Kiosks)}
                    // TODO temp icon
                    icon={<AdUnitsOutlinedIcon className={css.icon} />}
                />
                <SidebarIcon
                    show={healthCheckAccess}
                    active={healthCheckRouteActive}
                    tooltip={Localization.HealthCheck}
                    onClick={() => navigate(AppRoutes.HealthCheck)}
                    // TODO temp icon
                    icon={<MedicalServicesOutlinedIcon className={css.icon} />}
                />
                <SidebarIcon
                    show={responseLogAccess}
                    active={responseLogRouteActive}
                    tooltip={Localization.ResponseLog}
                    onClick={() => navigate(AppRoutes.ResponseLog)}
                    icon={<SyncAltIcon className={css.icon} />}
                />
                <SidebarIcon
                    show={userManagementAccess}
                    active={userRouteActive}
                    tooltip={Localization.UserManagement}
                    disableClickWhenActive={false}
                    onClick={() => {
                        if (!location.pathname.includes(PathName(AppRoutes.Users, UserRoutes.Maintenance))) {
                            navigate(AppRoutes.Users);
                        }
                    }}
                    icon={<UserIcon className={css.icon} />}
                />
                <SidebarIcon
                    show={settingsAccess}
                    active={settingsRouteActive}
                    tooltip={Localization.Settings}
                    onClick={() => setOpenDrawer(openDrawer === SidebarDrawerType.Settings ? SidebarDrawerType.None : SidebarDrawerType.Settings)}
                    disableClickWhenActive={false}
                    colorPathStroke
                    icon={<Cog className={css.icon} />}
                />
                <SidebarIcon
                    show={reportsAccess}
                    active={reportsRouteActive}
                    tooltip={Localization.Reports}
                    onClick={() => setOpenDrawer(openDrawer === SidebarDrawerType.Reports ? SidebarDrawerType.None : SidebarDrawerType.Reports)}
                    disableClickWhenActive={false}
                    icon={<PieChart className={css.icon} />}
                />
                {
                    // Display a divider between the Dashboard icon and the others to denote that it is a special icon that opens a new tab rather than screen in Support
                    dashboardAccess && <SidebarDivider />
                }
                <SidebarIcon
                    show={dashboardAccess}
                    active={false}
                    tooltip={Localization.Dashboard}
                    onClick={() => window.open(`${process.env.REACT_APP_DASHBOARD_URL}?sso=${UserData.dsim_userid}`, '_blank', 'noopener noreferrer')}
                    icon={<DashboardIcon className={css.icon} />}
                />
            </SidebarTopSection>
            {
                // We don't want to display divider below the Dashboard icon
                !dashboardAccess && <SidebarDivider />
            }
            <SidebarBottomSection>
                <SidebarLogout />
                <SidebarDivider />
                <SidebarLogo />
            </SidebarBottomSection>
        </Sidebar>
    );
};